import React, { useEffect, useState, Component } from 'react';
import { XIcon, ChevronDownIcon, ChevronUpIcon, Loader2Icon } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
export type TemplateBook = {
  book_id: string;
  title: string;
  description: string;
  cover_url: string;
  ageRange: string;
  readingLevel: string;
  tags: string[];
  previewPages?: string[];
  author?: string;
};
type BookDetailsPopupProps = {
  book: TemplateBook;
  onClose: () => void;
};
export const BookDetailsPopup = ({
  book,
  onClose
}: BookDetailsPopupProps) => {
  const [showTemplateSection, setShowTemplateSection] = useState(false);
  const [prompt, setPrompt] = useState(`Create a children's story about ${book.title.toLowerCase()} with themes of ${book.tags.join(', ').toLowerCase()}.`);
  const [artStyle, setArtStyle] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [storyCount, setStoryCount] = useState(0);
  const [isCountLoading, setIsCountLoading] = useState(false);
  const navigate = useNavigate();
  // Check if user is logged in and fetch story count
  useEffect(() => {
    const accessToken = localStorage.getItem('access_token');
    const userEmail = localStorage.getItem('user_email');
    if (accessToken && userEmail) {
      setIsLoggedIn(true);
      // Fetch story count
      const fetchStoryCount = async () => {
        setIsCountLoading(true);
        try {
          const response = await fetch(`https://storybunny.messagesimproved.com/get_story_generation_count?email=${encodeURIComponent(userEmail)}`, {
            method: 'GET'
          });
          if (response.ok) {
            const data = await response.json();
            setStoryCount(data.remaining_book_creations || 0);
          } else {
            console.error('Failed to fetch story count:', response.status);
          }
        } catch (error) {
          console.error('Error fetching story count:', error);
        } finally {
          setIsCountLoading(false);
        }
      };
      fetchStoryCount();
    }
  }, []);
  const handleReadBook = () => {
    // Direct to the full URL with book_id parameter
    window.location.href = `https://storybunny.ai/story?book_id=${book.book_id}`;
  };
  const fetchStoryPrompt = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`https://storybunny.messagesimproved.com/get_storybook_prompt?book_id=${book.book_id}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch prompt: ${response.status}`);
      }
      const data = await response.json();
      if (data.prompt) {
        setPrompt(data.prompt);
      }
      if (data.art_style) {
        setArtStyle(data.art_style);
      }
    } catch (err) {
      console.error('Error fetching story prompt:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch the original prompt');
    } finally {
      setIsLoading(false);
    }
  };
  const toggleTemplateSection = () => {
    if (!showTemplateSection) {
      fetchStoryPrompt();
    }
    setShowTemplateSection(!showTemplateSection);
  };
  const handleCreateFromTemplate = async () => {
    // If not logged in, redirect to sign-in page
    if (!isLoggedIn) {
      navigate('/create-account');
      return;
    }
    // If logged in but no credits, redirect to pricing page
    if (storyCount <= 0) {
      navigate('/pricing');
      return;
    }
    // Otherwise proceed with creating the story
    setIsCreating(true);
    setError(null);
    try {
      const url = new URL('https://storybunny.messagesimproved.com/generate_storybook');
      url.searchParams.append('prompt', prompt);
      if (artStyle) url.searchParams.append('art_style', artStyle);
      url.searchParams.append('parent', book.book_id); // Add parent book ID parameter
      const headers: Record<string, string> = {};
      const accessToken = localStorage.getItem('access_token');
      const userEmail = localStorage.getItem('user_email');
      if (accessToken && userEmail) {
        headers['Authorization'] = accessToken;
        headers['User-Email'] = userEmail;
      }
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}. Details: ${errorText}`);
      }
      const data = await response.json();
      if (!data.book_id) throw new Error('No book ID received from API');
      // Add a small delay before redirecting to ensure the loading state is visible
      setTimeout(() => {
        // Navigate to the story viewer with the new book ID
        window.location.href = `/story?book_id=${data.book_id}`;
      }, 500);
    } catch (err) {
      console.error('Error creating remixed story:', err);
      setError(err instanceof Error ? err.message : 'Failed to create remixed story');
      setIsCreating(false);
    }
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto" data-id="element-282">
      <div className="bg-white rounded-xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto relative" data-id="element-283">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-[#FFF0E6] transition-colors" aria-label="Close" disabled={isCreating} data-id="element-284">
          <XIcon size={24} className="text-[#6B4B3E]" data-id="element-285" />
        </button>
        <div className="flex flex-col md:flex-row gap-6" data-id="element-286">
          {/* Cover Image */}
          <div className="md:w-1/3" data-id="element-287">
            <div className="aspect-[3/4] relative rounded-lg overflow-hidden" data-id="element-288">
              <img src={book.cover_url} alt={`Cover for ${book.title}`} className="w-full h-full object-cover" onError={e => {
              e.currentTarget.src = '/bunny_face.png';
            }} data-id="element-289" />
            </div>
          </div>
          {/* Book Details */}
          <div className="md:w-2/3 pr-10" data-id="element-290">
            <h2 className="text-2xl font-bold text-[#6B4B3E] mb-2" data-id="element-291">
              {book.title}
            </h2>
            {book.author && book.author !== 'Story Bunny Team' && <p className="text-[#6B4B3E]/75 mb-4" data-id="element-292">By {book.author}</p>}
            <div className="mb-4" data-id="element-293">
              <h3 className="text-lg font-semibold text-[#6B4B3E] mb-2" data-id="element-294">
                About this story
              </h3>
              <p className="text-[#6B4B3E]/80" data-id="element-295">{book.description}</p>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-296">
              <div data-id="element-297">
                <h4 className="text-sm font-medium text-[#6B4B3E]/60" data-id="element-298">
                  Age Range
                </h4>
                <p className="text-[#6B4B3E]" data-id="element-299">{book.ageRange}</p>
              </div>
              <div data-id="element-300">
                <h4 className="text-sm font-medium text-[#6B4B3E]/60" data-id="element-301">
                  Reading Level
                </h4>
                <p className="text-[#6B4B3E]" data-id="element-302">{book.readingLevel}</p>
              </div>
            </div>
            {book.tags.length > 0 && <div className="mb-6" data-id="element-303">
                <h4 className="text-sm font-medium text-[#6B4B3E]/60 mb-2" data-id="element-304">
                  Tags
                </h4>
                <div className="flex flex-wrap gap-2" data-id="element-305">
                  {book.tags.map((tag, index) => <span key={index} className="px-3 py-1 bg-[#FFF0E6] text-[#6B4B3E] rounded-full text-sm" data-id="element-306">
                      {tag}
                    </span>)}
                </div>
              </div>}
          </div>
        </div>
        {/* Action Buttons */}
        <div className="mt-8 space-y-4" data-id="element-307">
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-308">
            {/* Primary CTA - Read Book */}
            <button className="px-6 py-3 bg-[#D4A088] text-white rounded-full hover:bg-[#C28B73] transition-colors w-full sm:w-auto disabled:opacity-70 disabled:cursor-not-allowed" onClick={handleReadBook} disabled={isCreating} data-id="element-309">
              Read Book
            </button>
            {/* Secondary CTA - Remix Book */}
            <div className="flex flex-col items-center" data-id="element-310">
              <button className="px-6 py-3 border-2 border-[#D4A088] text-[#D4A088] rounded-full hover:bg-[#FFF0E6] transition-colors flex items-center justify-center gap-2 w-full sm:w-auto disabled:opacity-70 disabled:cursor-not-allowed" onClick={toggleTemplateSection} disabled={isCreating} data-id="element-311">
                Remix Book
                {showTemplateSection ? <ChevronUpIcon size={18} data-id="element-312" /> : <ChevronDownIcon size={18} data-id="element-313" />}
              </button>
              <p className="text-xs text-center text-[#6B4B3E]/60 mt-2" data-id="element-314">
                Create a new book by changing the prompt used to create this one
              </p>
            </div>
          </div>
        </div>
        {/* Template Section - Collapsible */}
        {showTemplateSection && <div className="mt-6 p-4 bg-[#FFF0E6] rounded-lg border border-[#E5C1A9] transition-all" data-id="element-315">
            <h3 className="text-lg font-semibold text-[#6B4B3E] mb-3" data-id="element-316">
              Customize Your Story Prompt
            </h3>
            {isLoading ? <div className="flex items-center justify-center h-32" data-id="element-317">
                <Loader2Icon className="w-8 h-8 animate-spin text-[#D4A088]" data-id="element-318" />
                <span className="ml-2 text-[#6B4B3E]" data-id="element-319">
                  Loading original prompt...
                </span>
              </div> : error ? <div className="text-red-500 bg-red-50 p-4 rounded-lg mb-4" data-id="element-320">
                <p data-id="element-321">{error}</p>
                <p className="text-sm mt-2" data-id="element-322">Using a default prompt instead.</p>
              </div> : <>
                <div className="mb-4" data-id="element-323">
                  <label className="block text-sm font-medium text-[#6B4B3E] mb-2" data-id="element-324">
                    Story Prompt
                  </label>
                  <textarea value={prompt} onChange={e => setPrompt(e.target.value)} className="w-full h-32 p-3 border border-[#E5C1A9] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4A088] text-[#6B4B3E]" placeholder="Edit the story prompt here..." disabled={isCreating} data-id="element-325" />
                </div>
                {artStyle && <div className="mb-4" data-id="element-326">
                    <label className="block text-sm font-medium text-[#6B4B3E] mb-2" data-id="element-327">
                      Art Style
                    </label>
                    <textarea value={artStyle} onChange={e => setArtStyle(e.target.value)} className="w-full h-24 p-3 border border-[#E5C1A9] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4A088] text-[#6B4B3E]" placeholder="Edit the art style here..." disabled={isCreating} data-id="element-328" />
                  </div>}
              </>}
            <div className="flex justify-center" data-id="element-329">
              <button className={`px-6 py-3 bg-[#D4A088] text-white rounded-full hover:bg-[#C28B73] transition-colors disabled:opacity-70 disabled:cursor-not-allowed min-w-[160px] flex items-center justify-center`} onClick={handleCreateFromTemplate} disabled={isLoading || isCreating} data-id="element-330">
                {isCreating ? <>
                    <Loader2Icon className="inline-block mr-2 h-5 w-5 animate-spin" data-id="element-331" />
                    Creating...
                  </> : isLoading ? <>
                    <Loader2Icon className="inline-block mr-2 h-4 w-4 animate-spin" data-id="element-332" />
                    Loading...
                  </> : !isLoggedIn ? 'Sign In To Remix' : storyCount <= 0 ? 'Purchase Credits' : 'Create My Remix'}
              </button>
            </div>
          </div>}
      </div>
    </div>;
};